import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import cx from 'classnames';
import copy from 'copy-to-clipboard';
import { Link } from 'react-router-dom';
import { enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { Box, Tooltip, useTheme } from '@material-ui/core';
import Spinner from 'components/Spinner';

const useStyles = makeStyles(theme => ({
  pillBtn: {
    display: 'inline-flex',
    padding: '3px 10px',
    backgroundColor: '#000000',
    color: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 99,
    marginRight: 5,
    marginBottom: 10,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#9C8548',
    },
  },
  pillBtnDisabled: {
    display: 'inline-flex',
    padding: '3px 10px',
    backgroundColor: '#EBEBEB',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 99,
    marginRight: 5,
    marginBottom: 10,
    color: '#000000',
    cursor: 'default',
  },
  pillBtnHasError: {
    backgroundColor: theme.palette.error.main,
  },
  pillBtnNoLnk: {
    cursor: 'default',
    pointerEvents: 'none',
    backgroundColor: '#EBEBEB',
    color: '#000000',
  },
  pillBtnText: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 0,
    userSelect: 'none',
  },
  pillBtnTextDisabled: {
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 0,
  },
  pillBtnIcon: {
    fontSize: 13,
    marginRight: 4,
    display: 'flex',
    alignItems: 'center',
  },
  /**
   * Trailing icon (e.g. a right arrow on an external link). Kept separate from
   * `pillBtnIcon` so leading icons on Deal Detail pills do not pick up left margin.
   */
  pillBtnEndIcon: {
    fontSize: 13,
    marginLeft: 4,
    display: 'flex',
    alignItems: 'center',
  },
}));

const PillButtonTooltipWrapper = ({ tooltipText, children }) => {
  if (tooltipText) {
    return (
      <Tooltip arrow title={tooltipText} placement="top">
        {children}
      </Tooltip>
    );
  }
  return <>{children}</>;
};

const PillButton = ({
  type,
  clickToCopyContent,
  copySuccessMessage,
  externalUrl,
  appRoute,
  text,
  Icon,
  className,
  disabled,
  loading,
  tooltipText,
  hasError,
  EndIcon,
}) => {
  const classes = useStyles();
  const extraClasses = className ?? '';
  const dispatch = useDispatch();
  const theme = useTheme();

  const renderContent = () => (
    <>
      {Icon && (
        <span className={classes.pillBtnIcon}>
          <Icon fontSize="inherit" color="inherit" />
        </span>
      )}
      <span
        className={cx(classes.pillBtnText, {
          [`${classes.pillBtnTextDisabled}`]: disabled,
        })}
      >
        <Box
          style={{ display: 'flex', alignItems: 'center', color: hasError ? 'white' : 'inherit' }}
        >
          {text}
          {loading && (
            <Box ml={1} style={{ position: 'relative', top: 2 }}>
              <Spinner size={12} color="primary" />
            </Box>
          )}
          {EndIcon && (
            <span className={classes.pillBtnEndIcon}>
              <EndIcon fontSize="inherit" color="inherit" />
            </span>
          )}
        </Box>
      </span>
    </>
  );

  const handleClickToCopy = () => {
    copy(clickToCopyContent, {
      onCopy: () => {
        dispatch(enqueueSuccessSnackbar(copySuccessMessage || 'Copied to Clipboard'));
      },
      format: 'text/plain',
    });
  };

  return (
    <PillButtonTooltipWrapper tooltipText={tooltipText}>
      <div>
        {type === 'externalUrl' && (
          <a
            href={externalUrl}
            className={cx(classes.pillBtn, extraClasses, {
              [`${classes.pillBtnDisabled}`]: disabled,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            {renderContent()}
          </a>
        )}
        {type === 'appRoute' && (
          <Link
            to={appRoute}
            className={cx(classes.pillBtn, extraClasses, {
              [`${classes.pillBtnDisabled}`]: disabled,
            })}
          >
            {renderContent()}
          </Link>
        )}
        {type === 'clickToCopy' && (
          <span
            onClick={handleClickToCopy}
            className={cx(classes.pillBtn, extraClasses, {
              [`${classes.pillBtnDisabled}`]: disabled,
            })}
          >
            {renderContent()}
          </span>
        )}
        {type === 'plainText' && (
          <span
            className={cx(classes.pillBtn, classes.pillBtnNoLnk, extraClasses, {
              [`${classes.pillBtnDisabled}`]: disabled,
              [`${classes.pillBtnHasError}`]: hasError,
            })}
            style={{ opacity: hasError ? 0.8 : 1 }}
          >
            {renderContent()}
          </span>
        )}
      </div>
    </PillButtonTooltipWrapper>
  );
};

PillButton.propTypes = {
  type: PropTypes.oneOf(['appRoute', 'externalUrl', 'clickToCopy', 'plainText']).isRequired,
  clickToCopyContent: PropTypes.string,
  copySuccessMessage: PropTypes.string,
  externalUrl: PropTypes.string,
  appRoute: PropTypes.string,
  text: PropTypes.string.isRequired,
  Icon: PropTypes.elementType,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  tooltipText: PropTypes.string,
  /** Optional icon rendered after the label (e.g. ArrowForward on an outbound link). */
  EndIcon: PropTypes.elementType,
};

export default PillButton;
